import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, Megaphone, Disc3, MapPin, Globe } from "lucide-react";

const KIND_META = {
    promoter: { label: "Promoter", Icon: Megaphone },
    label: { label: "Record Label", Icon: Disc3 },
};

/**
 * Minimum public entity page for Promoter + Label.
 * Discovery Agent needs to understand the shape — not a rich landing.
 */
export default function OrgPublic() {
    const { kind, orgId } = useParams();
    const meta = KIND_META[kind] || { label: "Organisation", Icon: Building2 };
    const Icon = meta.Icon;
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/${kind}s/${orgId}`);
                setOrg(r.data);
            } catch { setOrg(null); }
            setLoading(false);
        })();
    }, [kind, orgId]);

    if (loading) return <div className="p-10 font-mono text-xs text-[#666]">Loading…</div>;
    if (!org) return <div className="p-10 text-center"><div className="text-2xl font-display uppercase">Not found</div><Link to="/" className="text-[#ff4400] font-mono text-xs uppercase mt-4 inline-block">← Home</Link></div>;

    const verified = (org.verification || {}).status === "verified";

    return (
        <div className="pm-fade" data-testid={`public-${kind}-page`}>
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-10">
                <div className="max-w-5xl mx-auto flex items-start gap-6">
                    <div className="w-20 h-20 md:w-28 md:h-28 border border-[#2a2a2a] flex items-center justify-center bg-[#141414] shrink-0">
                        <Icon className="w-10 h-10 text-[#ff4400]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">{meta.label}</div>
                        <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl truncate" data-testid={`public-${kind}-name`}>
                            {org.name}
                        </h1>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-mono uppercase text-[#a3a3a3]">
                            {(org.city || org.country) && (
                                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[org.city, org.country].filter(Boolean).join(", ")}</span>
                            )}
                            {org.website && (
                                <a href={org.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#ff4400]">
                                    <Globe className="w-3 h-3" /> Website
                                </a>
                            )}
                            {verified && (
                                <span className="text-[#00e676]" data-testid={`public-${kind}-verified`}>· Verified</span>
                            )}
                        </div>
                    </div>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                <section className="md:col-span-2 space-y-4">
                    <div className="border border-[#2a2a2a] p-5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">About</div>
                        <p className="mt-2 text-sm text-[#a3a3a3]" data-testid={`public-${kind}-description`}>
                            {org.description || "No description yet."}
                        </p>
                    </div>
                    {kind === "label" && (
                        <div className="border border-[#2a2a2a] p-5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Genres</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(org.genres || []).length === 0 ? <span className="text-[10px] font-mono uppercase text-[#666]">—</span> : (org.genres || []).map((g, i) => (
                                    <span key={i} className="text-[10px] font-mono uppercase border border-[#2a2a2a] px-2 py-1">{g}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {kind === "promoter" && (
                        <div className="border border-[#2a2a2a] p-5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Primary markets</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {(org.markets || []).length === 0 ? <span className="text-[10px] font-mono uppercase text-[#666]">—</span> : (org.markets || []).map((m, i) => (
                                    <span key={i} className="text-[10px] font-mono uppercase border border-[#2a2a2a] px-2 py-1">{m}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
                <aside className="space-y-4">
                    <div className="border border-[#2a2a2a] p-5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Contact</div>
                        <div className="mt-2 text-xs font-mono text-[#a3a3a3]">{org.contact_email || "—"}</div>
                    </div>
                </aside>
            </main>
        </div>
    );
}
